<template>
  <div class="space-y-6">
    <div class="text-center">
      <h2 class="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-white mb-2">
        {{ $t('auth.resetPasswordTitle') }}
      </h2>
      <p v-if="validToken && username" class="text-sm text-neutral-600 dark:text-neutral-300">
        {{ $t('auth.resetPasswordSubtitle', { username }) }}
      </p>
    </div>

    <div v-if="validating" class="text-center text-neutral-500">{{ $t('common.loading') }}</div>

    <div v-else-if="!validToken" class="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-center">
      <p class="text-sm text-red-700 dark:text-red-300">{{ $t('auth.resetPasswordInvalidToken') }}</p>
      <router-link to="/auth/forgot-password" class="inline-block mt-3 text-sm text-primary-600 dark:text-primary-400">
        {{ $t('auth.forgotPassword') }}
      </router-link>
    </div>

    <div v-else-if="success" class="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 text-center">
      <p class="text-sm text-green-700 dark:text-green-300">{{ $t('auth.resetPasswordSuccess') }}</p>
      <router-link to="/auth/login" class="inline-block mt-3 text-sm text-primary-600 dark:text-primary-400">
        {{ $t('auth.backToLogin') }}
      </router-link>
    </div>

    <form v-else class="space-y-4" @submit.prevent="submit">
      <div>
        <label for="password" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          {{ $t('auth.resetPasswordNew') }}
        </label>
        <input
          id="password"
          v-model="password"
          type="password"
          required
          minlength="6"
          autocomplete="new-password"
          class="input w-full"
        />
      </div>
      <div>
        <label for="confirm" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          {{ $t('auth.resetPasswordConfirm') }}
        </label>
        <input
          id="confirm"
          v-model="confirm"
          type="password"
          required
          minlength="6"
          autocomplete="new-password"
          class="input w-full"
        />
      </div>
      <p class="text-xs text-neutral-500 dark:text-neutral-400">{{ $t('auth.passwordRequirements') }}</p>

      <div v-if="error" class="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
        <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
      </div>

      <button type="submit" class="btn-primary w-full" :disabled="loading">
        {{ loading ? $t('common.saving') : $t('auth.resetPasswordSubmit') }}
      </button>
    </form>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services/api'

const { t } = useI18n()
const route = useRoute()

const token = ref('')
const username = ref('')
const password = ref('')
const confirm = ref('')
const validating = ref(true)
const validToken = ref(false)
const loading = ref(false)
const error = ref('')
const success = ref(false)

const validate = async () => {
  validating.value = true
  try {
    const res = await apiService.auth.validateResetToken(token.value)
    validToken.value = !!res.data?.valid
    username.value = res.data?.username || ''
  } catch {
    validToken.value = false
  } finally {
    validating.value = false
  }
}

const submit = async () => {
  if (password.value !== confirm.value) {
    error.value = t('auth.resetPasswordMismatch')
    return
  }
  loading.value = true
  error.value = ''
  try {
    const res = await apiService.auth.resetPassword({
      token: token.value,
      password: password.value,
    })
    if (res.success) {
      success.value = true
    } else {
      error.value = res.message || t('auth.resetPasswordError')
    }
  } catch (e) {
    error.value = e?.message || t('auth.resetPasswordError')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  token.value = String(route.query.token || '').trim()
  if (!token.value) {
    validating.value = false
    validToken.value = false
    return
  }
  validate()
})
</script>
